import {getCssSize, CSSDimension, Integer, Box, HStack, Right, Show} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {MouseEvent, TdHTMLAttributes} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {IconButton} from '../../IconButton/IconButton';
import {Tooltip} from '../../Tooltip/Tooltip';
import {TableRowAction} from '../types/TableRowAction';
import {TableVariant} from '../types/TableVariant';
import {useTableContext} from '../useTableContext';
import {TableRowActionsDropdown} from './TableRowActionsDropdown';

export interface TableRowActionsData {
  primary?: TableRowAction[];
  secondary?: TableRowAction[];
}

interface TableRowActionsProps extends TestIdProps {
  actions?: TableRowActionsData;
  actionsWidth?: CSSDimension | Integer;
}

type TDActionsStyledProps = {
  $actionsWidth: TableRowActionsProps['actionsWidth'];
  $variant: TableVariant;
} & TdHTMLAttributes<HTMLTableCellElement>;

const tdActionsVariants = cva('h-[2.688rem] min-h-[2.688rem]', {
  variants: {
    variant: {
      bordered: 'border-l border-l-[var(--color-general-separator)]',
      plain: '',
      stripped: '',
    },
  },
});

export function TableRowActions(props: TableRowActionsProps) {
  const context = useTableContext();

  const handleTdClick = (event: MouseEvent<HTMLTableCellElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  if (!props.actions?.primary?.length && !props.actions?.secondary?.length) {
    return (
      <TDActionsStyled
        $variant={context.variant}
        $actionsWidth={props.actionsWidth}
        onClick={handleTdClick}
      />
    );
  }

  return (
    <TDActionsStyled
      $variant={context.variant}
      $actionsWidth={props.actionsWidth}
      onClick={handleTdClick}
    >
      <Box paddingHorizontal={2} paddingVertical={1}>
        <Right>
          <HStack data-testid={suffixTestId('actions', props)}>
            {props.actions.primary?.map((primaryAction, index) => (
              <Tooltip
                key={primaryAction.title}
                isDisabled={!primaryAction.title}
                label={primaryAction.title}
                description={primaryAction.description}
                placement="top-start"
              >
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    primaryAction.onClick();
                  }}
                  icon={primaryAction.icon}
                  isDisabled={primaryAction.isDisabled}
                  severity={primaryAction.severity}
                  data-testid={suffixTestId(`[${index}]`, props)}
                />
              </Tooltip>
            ))}

            <Show when={!!props.actions.secondary?.length}>
              <TableRowActionsDropdown
                actions={props.actions.secondary}
                data-testid={props['data-testid']}
              />
            </Show>
          </HStack>
        </Right>
      </Box>
    </TDActionsStyled>
  );
}

export function TDActionsStyled(props: TDActionsStyledProps) {
  const htmlProps = {...props};
  Reflect.deleteProperty(htmlProps, '$actionsWidth');
  Reflect.deleteProperty(htmlProps, '$variant');

  return (
    <td
      {...htmlProps}
      className={cn(tdActionsVariants({variant: props.$variant}), props.className)}
      style={{width: getCssSize(props.$actionsWidth) ?? '1px', ...props.style}}
    />
  );
}
